<template>
	<div
		class="empty-state"
		v-if="!messages.length && !loading"
		v-t="'groupsettings_mediatray_imagesandvideos_none'"
	/>
	<ScrollToLoad
		class="group-media-photos"
		:loading="loading"
		@bottom="loadMoreMessages"
	>
		<div class="photo-item-list">
			<div class="photo-item" v-for="item in visibleItems" :key="item.id">
				<div class="photo-item-thumb" @click="onMediaClick(item)">
					<VisualMedia
						:media="item"
						isSent
						showVideoThumbnail
						:playVideo="false"
					/>
				</div>
				<div class="photo-item-info" @click="showUserModal(item.user.id)">
					<ProfilePic
						class="photo-item-profile"
						:user="item.user"
						size="38px"
					/>
					<div class="photo-user">
						<div class="photo-username" :title="item.user.fullname">
							<UserName :user="item.user" />
						</div>
						<div class="photo-stats">
							<div class="photo-size" v-text="item.size" />
							<div
								class="photo-date"
								:title="item.created_at"
								v-text="item.fromNow"
							/>
						</div>
						<ViewInChat :id="item.messageId" @click.stop />
					</div>
				</div>
			</div>
		</div>
		<template #spinner>
			<IosSpinner
				v-if="loading"
				top="47%"
				left="0"
				right="0"
				style="margin-left: auto; margin-right: auto"
			>
				<template #title>{{ $t('common_loader_loading_title') }}</template>
			</IosSpinner>
		</template>
	</ScrollToLoad>
</template>

<script setup>
import { computed, ref, onMounted, provide } from 'vue'
import { getCancelToken } from '@/helpers/api'
import { dateFormat } from '@/helperMethods'
import ViewInChat from '@/components/ViewInChat'
import {
	UserName,
	ProfilePic,
	ScrollToLoad,
	useMediaModal,
} from '@/components/common'
import VisualMedia from '@/components/common/VisualMedia'
import { fetchHistoryWithoutStore } from '@/api/chat'
import { normalizeMessage } from '@/store/messages'
import { showUserModal } from '@/helpers/utils'
import moment from 'moment'
import IosSpinner from '@/components/IosSpinner'

const VISIBLE_PAGE_SIZE = 18

const props = defineProps({ chatId: [String, Number] })
provide('maxMediaSize', 224) // Used by VisualMedia.vue

const modal = useMediaModal()

const loading = ref(false)
const messages = ref([])
const currentPage = ref(0)

let latest = null
let cancelToken = null
let everythingLoaded = false

const mediaItems = computed(() =>
	messages.value
		.map(({ id, user, attachments }) => {
			return attachments
				.filter((m) => m.isPhoto || m.isVideo)
				.map((media) => ({
					...media,
					messageId: id,
					created_at: dateFormat(media.created_at),
					fromNow: moment.utc(media.created_at).fromNow(),
					user,
				}))
		})
		.flat()
)

const visibleCount = computed(() => VISIBLE_PAGE_SIZE * currentPage.value)
const visibleItems = computed(() =>
	mediaItems.value.slice(0, visibleCount.value)
)

const loadMoreMessages = (isAtBottom) => {
	if (!isAtBottom || !messages.value.length) return false
	getMessageHistory()
}

const onMediaClick = (item) => {
	const message = messages.value.find((m) => m.id === item.message_id)
	modal.message(message, item.id)
}

const getMessageHistory = async () => {
	if (loading.value) return

	loading.value = true

	if (mediaItems.value.length > visibleCount.value) {
		// we already have more items loaded, just need to display them
		loading.value = false
		currentPage.value += 1
		return
	}

	if (everythingLoaded) {
		// we've already got everything from the server so good to bail
		loading.value = false
		return
	}

	const payload = { mediatypes: 'PHOTO,VIDEO', bubble_id: props.chatId }

	if (latest) {
		payload.latest = latest
	}

	if (cancelToken) {
		cancelToken.cancel()
	}

	cancelToken = getCancelToken()

	try {
		const data = await fetchHistoryWithoutStore(payload, cancelToken)
		if (!data.messages.length) {
			loading.value = false
			everythingLoaded = true
			return
		}

		messages.value = messages.value.concat(data.messages.map(normalizeMessage))
		currentPage.value += 1

		if (data.pagesize === data.messages.length) {
			latest = data.messages.at(-1).id
		} else {
			everythingLoaded = true
		}
		loading.value = false
	} catch (e) {
		console.error(e)
		loading.value = false
	}
}

onMounted(() => {
	getMessageHistory()
})
</script>

<style lang="scss" scoped>
@import 'src/sass/variables';

.group-media-photos {
	max-height: calc(100vh - 284px);
	overflow-y: auto;
	.photo-item-list {
		display: grid;
		grid-template-columns: 1fr 1fr 1fr;
		gap: 16px;
		padding-bottom: 40px;
	}
	:deep(.pronto-spinner) {
		margin-top: 24px;
		font-size: 40px;
	}
}

.photo-item {
	cursor: pointer;
	padding: 12px;
	border: 1px solid #e7e7e8;
	border-radius: 10px;
	height: 300px;
	width: 250px;
	display: flex;
	flex-direction: column;
	gap: 12px;
	.photo-item-thumb {
		width: 100%;
		height: 200px;
		@include flex-center;
		background: var(--neutral-200);
		border-radius: 6px;
		overflow: hidden;
	}
	.photo-item-info {
		@include flex(12px);
		.photo-item-profile {
			flex-shrink: 0;
		}
		.photo-user {
			@include flex-column;
			flex-grow: 1;
			max-width: calc(100% - 50px);
			.photo-username {
				@include fontFamily(900);
				font-size: 14px;
				.user-display-name {
					@include clamp(1);
				}
			}
			.photo-stats {
				@include flex(8px);
				.photo-size,
				.photo-date {
					font-size: 14px;
					color: var(--neutral-300);
				}
				.photo-size {
					flex-shrink: 0;
					white-space: nowrap;
				}
				.photo-date {
					@include ellipsis;
				}
			}
		}
	}
}

.missing-img {
	font-size: 16px !important;
}

.empty-state {
	position: absolute;
	top: 46%;
	left: calc(46% - 125px);
	width: 380px;
	font-family: 'Avenir-Medium';
	font-size: 28px;
	color: #4f5053;
	text-align: center;
}
</style>
<style lang="scss">
@import 'src/sass/variables';

.app-layout {
	&.announcement {
		.group-media-photos {
			max-height: calc(100vh - 414px);
		}
	}
}

@include overrideScheme('dark') {
	.photo-item {
		background-color: var(--neutral-0) !important;
		border-color: var(--neutral-40040) !important;
		.photo-stats {
			.photo-size,
			.photo-date {
				font-size: 14px;
				color: var(--neutral-400) !important;
			}
		}
	}
	.group-media-photos {
		.ios-spinner {
			background: var(--neutral-100) !important;
		}
	}
}
</style>
